import type { UnsyncedOrder } from '../../../../types/api.types';
import {
    formatDateTime,
    formatErrorType,
    formatPrice,
    getNetworkBadge,
    getOrderHeaderId,
    getOrderItemCount,
    getOrderItemsTotal,
} from './unsyncedOrderUtils';

interface UnsyncedOrderRowProps {
    order: UnsyncedOrder;
    isSelected: boolean;
    isExpanded: boolean;
    onToggleSelect: () => void;
    onToggleExpand: () => void;
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
    return (
        <svg
            className={`h-4 w-4 text-[#64748b] transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
        >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
    );
}

export default function UnsyncedOrderRow({
    order,
    isSelected,
    isExpanded,
    onToggleSelect,
    onToggleExpand,
}: UnsyncedOrderRowProps) {
    const errorLabel = formatErrorType(order.errorType);
    const networkBadge = getNetworkBadge(order.orderNetworkStatus);
    const headerId = getOrderHeaderId(order);
    const itemCount = getOrderItemCount(order);
    const itemsTotal = getOrderItemsTotal(order);
    const itemsPanelId = `order-items-${order._id}`;

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onToggleSelect}
            onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    onToggleSelect();
                }
            }}
            className={`rounded-lg border transition-colors cursor-pointer ${
                isSelected
                    ? 'border-[#6e64f0] bg-[#fafafe] ring-1 ring-[#6e64f0]'
                    : 'border-[#e2e8f0] hover:bg-[#fafafa]'
            }`}
        >
            <div className="flex items-start gap-3 p-3">
                <input
                    type="checkbox"
                    aria-label={`Select order ${order.orderID}`}
                    checked={isSelected}
                    onChange={onToggleSelect}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-0.5 h-4 w-4 rounded border-[#cbd5e1] text-[#6e64f0] focus:ring-[#6e64f0]"
                />

                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                            <p
                                className="text-sm font-medium text-[#1e293b] truncate"
                                title={headerId}
                            >
                                {headerId}
                            </p>
                            {order.offlineOrderID && (
                                <p
                                    className="font-mono text-xs text-[#64748b] truncate mt-0.5"
                                    title={order.orderID}
                                >
                                    {order.orderID}
                                </p>
                            )}
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                            {networkBadge && (
                                <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${networkBadge.badge}`}
                                >
                                    {networkBadge.label}
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
                                aria-expanded={isExpanded}
                                aria-controls={itemsPanelId}
                                aria-label={
                                    isExpanded
                                        ? `Hide items for order ${headerId}`
                                        : `Show items for order ${headerId}`
                                }
                                className="flex h-8 w-8 items-center justify-center rounded-lg text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#1e293b] transition-colors"
                            >
                                <ChevronIcon expanded={isExpanded} />
                            </button>
                        </div>
                    </div>

                    <p className="text-xs text-[#64748b] mt-1.5">
                        Created {formatDateTime(order.createdAt)}
                        {errorLabel && (
                            <span className="text-[#b45309]"> · {errorLabel}</span>
                        )}
                    </p>

                    {!isExpanded && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
                            className="mt-2 text-xs text-[#475569] hover:text-[#1e293b] transition-colors"
                        >
                            {itemCount === 0
                                ? 'No items'
                                : `${itemCount} item${itemCount !== 1 ? 's' : ''} · ${formatPrice(itemsTotal)}`}
                        </button>
                    )}
                </div>
            </div>

            {isExpanded && (
                <div
                    id={itemsPanelId}
                    onClick={(e) => e.stopPropagation()}
                    className="border-t border-[#f1f5f9] px-3 pb-3 pt-2 ml-10"
                >
                    {itemCount > 0 ? (
                        <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1">
                            {order.items.map((item, index) => (
                                <div
                                    key={`${order._id}-item-${index}`}
                                    className="flex items-start justify-between gap-3 text-sm"
                                >
                                    <span
                                        className="text-[#1e293b] truncate"
                                        title={item.name}
                                    >
                                        {item.name}
                                    </span>
                                    <span className="flex-shrink-0 text-[#475569] tabular-nums">
                                        {formatPrice(item.price)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-xs text-[#94a3b8]">No items</p>
                    )}

                    {itemCount > 0 && (
                        <div className="flex items-center justify-between gap-3 mt-2 pt-2 border-t border-[#f1f5f9] text-xs font-medium text-[#475569]">
                            <span>
                                {itemCount} item{itemCount !== 1 ? 's' : ''}
                            </span>
                            <span className="tabular-nums">{formatPrice(itemsTotal)}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
